import { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaSave } from 'react-icons/fa';
import AssetUploadRow from '../../component/AssetUploadRow';
import { useWebsiteSettings } from '../../hooks/useWebsiteSettings';
import { apiHost } from '../../helper/apiHost';

const isFaviconFile = (file) => /\.(ico|png|svg)$/i.test(file.name);

const WebsiteSettings = ({ data }) => {
    const header = {
        headers: { Authorization: `Bearer ${data.token}` },
        withCredentials: true,
    };

    const { settings, loading, reloadSettings } = useWebsiteSettings(data.token);

    const [institutionName, setInstitutionName] = useState('');
    const [systemTitle, setSystemTitle] = useState('');
    const [footerText, setFooterText] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [faviconFile, setFaviconFile] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingFavicon, setUploadingFavicon] = useState(false);
    const [savingText, setSavingText] = useState(false);
    const [previewKey, setPreviewKey] = useState(0);
    const [faviconPreviewKey, setFaviconPreviewKey] = useState(0);

    const activeLogoUrl = settings.logo?.url
        ? `${apiHost}${settings.logo.url}?t=${previewKey}`
        : null;

    const activeFaviconUrl = settings.favicon?.url
        ? `${apiHost}${settings.favicon.url}?t=${faviconPreviewKey}`
        : null;

    useEffect(() => {
        setInstitutionName(settings.institutionName || '');
        setSystemTitle(settings.systemTitle || '');
        setFooterText(settings.footerText || '');
    }, [settings]);

    const handleSaveText = async () => {
        if (!institutionName.trim() || !systemTitle.trim() || !footerText.trim()) {
            Swal.fire('Gagal', 'Semua field informasi website wajib diisi.', 'error');
            return;
        }

        setSavingText(true);
        try {
            await axios.patch(
                'api/website-settings',
                {
                    institutionName: institutionName.trim(),
                    systemTitle: systemTitle.trim(),
                    footerText: footerText.trim(),
                },
                header,
            );
            await reloadSettings();
            window.dispatchEvent(new Event('website-settings:updated'));
            Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Informasi website berhasil disimpan.',
                timer: 1400,
                showConfirmButton: false,
            });
        } catch (err) {
            Swal.fire(
                'Gagal',
                err?.response?.data?.message || 'Tidak bisa menyimpan pengaturan.',
                'error',
            );
        } finally {
            setSavingText(false);
        }
    };

    const handleUploadLogo = async () => {
        if (!logoFile) {
            Swal.fire('Gagal', 'Pilih file logo dulu.', 'error');
            return;
        }

        const formData = new FormData();
        formData.append('logo', logoFile);

        setUploadingLogo(true);
        try {
            await axios.post('api/website-settings/logo', formData, header);
            setLogoFile(null);
            setPreviewKey((k) => k + 1);
            await reloadSettings();
            window.dispatchEvent(new Event('website-logo:updated'));

            Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Logo website berhasil diupload.',
                timer: 1400,
                showConfirmButton: false,
            });
        } catch (err) {
            Swal.fire(
                'Gagal',
                err?.response?.data?.message || 'Tidak bisa upload logo.',
                'error',
            );
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleUploadFavicon = async () => {
        if (!faviconFile) {
            Swal.fire('Gagal', 'Pilih file favicon dulu.', 'error');
            return;
        }

        const formData = new FormData();
        formData.append('favicon', faviconFile);

        setUploadingFavicon(true);
        try {
            await axios.post('api/website-settings/favicon', formData, header);
            setFaviconFile(null);
            setFaviconPreviewKey((k) => k + 1);
            await reloadSettings();
            window.dispatchEvent(new Event('website-favicon:updated'));

            Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Favicon website berhasil diupload.',
                timer: 1400,
                showConfirmButton: false,
            });
        } catch (err) {
            Swal.fire(
                'Gagal',
                err?.response?.data?.message || 'Tidak bisa upload favicon.',
                'error',
            );
        } finally {
            setUploadingFavicon(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-hairline border-t-primary" />
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-3xl space-y-4 pb-8">
            <section className="card p-5 md:p-6">
                <h3 className="mb-4 text-base font-semibold text-ink dark:text-white">
                    Informasi Website
                </h3>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="institutionName" className="mb-1.5 block text-sm font-medium text-ink dark:text-white">
                            Nama institusi
                        </label>
                        <input
                            id="institutionName"
                            value={institutionName}
                            onChange={(e) => setInstitutionName(e.target.value)}
                            className="input w-full"
                            placeholder="Universitas Pertiba"
                            disabled={savingText}
                        />
                    </div>

                    <div>
                        <label htmlFor="systemTitle" className="mb-1.5 block text-sm font-medium text-ink dark:text-white">
                            Judul sistem
                        </label>
                        <input
                            id="systemTitle"
                            value={systemTitle}
                            onChange={(e) => setSystemTitle(e.target.value)}
                            className="input w-full"
                            placeholder="Letter of Acceptance"
                            disabled={savingText}
                        />
                    </div>

                    <div>
                        <label htmlFor="footerText" className="mb-1.5 block text-sm font-medium text-ink dark:text-white">
                            Teks footer
                        </label>
                        <textarea
                            id="footerText"
                            value={footerText}
                            onChange={(e) => setFooterText(e.target.value)}
                            className="input min-h-[88px] w-full resize-y !h-auto py-2.5"
                            placeholder="© Universitas Pertiba. All rights reserved."
                            disabled={savingText}
                            rows={3}
                        />
                    </div>
                </div>

                <div className="mt-5 flex justify-end">
                    <button
                        type="button"
                        onClick={handleSaveText}
                        disabled={savingText}
                        className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        <FaSave className="text-xs" />
                        {savingText ? 'Menyimpan...' : 'Simpan'}
                    </button>
                </div>
            </section>

            <section className="card p-5 md:p-6">
                <h3 className="mb-4 text-base font-semibold text-ink dark:text-white">
                    Logo & Favicon
                </h3>

                <div className="space-y-3">
                    <AssetUploadRow
                        title="Logo website"
                        hint="PNG atau JPG · maks. 1MB"
                        currentUrl={activeLogoUrl}
                        file={logoFile}
                        onFileChange={setLogoFile}
                        onUpload={handleUploadLogo}
                        uploading={uploadingLogo}
                        previewBoxClassName="h-12 w-28"
                    />

                    <AssetUploadRow
                        title="Favicon"
                        hint="ICO, PNG, atau SVG · maks. 512KB"
                        currentUrl={activeFaviconUrl}
                        file={faviconFile}
                        onFileChange={setFaviconFile}
                        onUpload={handleUploadFavicon}
                        uploading={uploadingFavicon}
                        accept=".ico,.png,.svg,image/x-icon,image/png,image/svg+xml"
                        validateFile={isFaviconFile}
                        previewBoxClassName="h-10 w-10"
                        imageClassName="h-7 w-7 object-contain"
                    />
                </div>
            </section>
        </div>
    );
};

export default WebsiteSettings;
